import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import AddLink from '../ui/AddLink';
import { confirmAction } from '../ui/Confirm';
import { ErrorNotice } from '../ui/States';
import { EventDetail } from '../../lib/events';
import { errorMessage } from '../../lib/programs';
import type { SimplePanelProps } from './adapt';

/**
 * The PI meeting (or dinner) that belongs to this national meeting. Existing ones are listed under "Related" in
 * the Overview's event details; adding one is a link in the "Add to this tab" bar.
 */
export default function SubEventsPanel({ eventId, canEdit }: SimplePanelProps) {
  const navigate = useNavigate();
  const q = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const add = useMutation(
    async () => {
      const e = q.data!.event;
      const first = e.days[0];
      const res = await api.post('/events', {
        title: `PI meeting, ${e.title}`,
        kind: 'pi_meeting',
        mode: 'in_person',
        city: e.city,
        venue: e.venue,
        days: [{ date: first.date, startTime: first.startTime, endTime: first.endTime }],
        parentEventId: e.id,
      });
      return res.data.data as EventDetail;
    },
    { onSuccess: (d) => navigate(`/events/${d.event.id}`) },
  );
  if (!q.data || !canEdit || q.data.event.kind !== 'national') return null;
  return (
    <>
      <AddLink
        label="PI meeting"
        onClick={async () => {
          if (add.isLoading) return;
          if (
            await confirmAction(
              'Add a PI meeting to this event? The PI meeting or dinner gets its own page: room, link for PIs joining online, dinner and agenda. It opens once it is made.',
              'Add the PI meeting',
            )
          )
            add.mutate();
        }}
      />
      {add.error ? (
        <div className="mt-6">
          <ErrorNotice>{errorMessage(add.error, 'Could not add the PI meeting.')}</ErrorNotice>
        </div>
      ) : null}
    </>
  );
}
